import { useState } from "react";
import { Loader } from "@mantine/core";
import { IconFileSpreadsheet } from "@tabler/icons-react";

import { useOrganizationQuery } from "../api/queries";
import { useAtomValue } from "jotai";
import { ExcelUploadWithMapping } from "./ExcelUploadWithMapping";
import { PriorityConnection } from "./PriorityConnection";
import { StripeConnection } from "./StripeConnection";
import { MorningConnection } from "./MorningConnection";
import { ViewzConnection } from "./ViewzConnection";
import { NonTrialScope } from "./auth/NonTrialScope";
import { TrialScope } from "./auth/TrialScope";
import {
  IntegrationConnectButton,
  IntegrationConnectedButton,
  IntegrationDisabledButton,
} from "./integrations/IntegrationButtons";
import { IntegrationLogo } from "./integrations/IntegrationLogo";
import { orgIdAtom } from "../store/authAtoms";

const VISITT_LTD_ORG_ID = 3;

/** Viewz shares Priority for PDFs, so Priority/Excel must not block Connect. */
export function isViewzConnectBlocked(
  connected: string | null | undefined,
): boolean {
  return (
    connected != null &&
    connected !== "Excel" &&
    connected !== "Priority" &&
    connected !== "Viewz"
  );
}

/** QB / Xero / Stripe / Morning cannot share an invoice source. Excel can be replaced. */
export function isExclusiveApiConnectBlocked(
  connected: string | null | undefined,
): boolean {
  return connected != null && connected !== "Excel";
}

/** Priority PDFs stay connected beside Viewz/Excel when the URL is still set. */
export function isPriorityShownConnected(
  connected: string | null | undefined,
  hasPriorityUrl: boolean,
): boolean {
  if (connected === "Priority") {
    return true;
  }
  return Boolean(hasPriorityUrl) && (connected === "Viewz" || connected === "Excel");
}

export function AccountingSoftwareSettings() {
  const { data: organization, isLoading } = useOrganizationQuery();
  const orgId = useAtomValue(orgIdAtom);
  const [showExcelUpload, setShowExcelUpload] = useState(false);

  const installQuickBooks = () => {
    window.location.href = organization?.quickbooks_authorization_url || "";
  };

  const installXero = () => {
    window.location.href = organization?.xero_authorization_url || "";
  };

  const connected = organization?.accounting_software ?? null;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader />
      </div>
    );
  }

  if (showExcelUpload) {
    return (
      <div className="flex w-full flex-col items-center space-y-4 py-2">
        <ExcelUploadWithMapping
          onSuccess={() => setShowExcelUpload(false)}
          onCancel={() => setShowExcelUpload(false)}
        />
      </div>
    );
  }

  const qbConnected = connected === "QuickBooks";
  const xeroConnected = connected === "Xero";
  const stripeConnected = connected === "Stripe";
  const morningConnected = connected === "Morning";
  const viewzConnected = connected === "Viewz";
  const excelConnected = connected === "Excel";
  const exclusiveApiLocked = isExclusiveApiConnectBlocked(connected);
  const priorityShownConnected = isPriorityShownConnected(
    connected,
    Boolean(organization?.has_priority_url),
  );
  const showViewz = orgId === VISITT_LTD_ORG_ID || viewzConnected;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3">
        <TrialScope>
          <div className="rounded-lg border border-amber-100 bg-amber-50/80 px-4 py-3">
            <p className="text-sm font-medium text-amber-800">
              Integrations are not available in trial.
            </p>
          </div>
        </TrialScope>
        <IntegrationRow
          icon={<IntegrationLogo id="quickbooks" />}
          title="QuickBooks Online"
          action={
            <>
              <TrialScope>
                <IntegrationDisabledButton />
              </TrialScope>
              <NonTrialScope>
                {qbConnected ? (
                  <IntegrationConnectedButton />
                ) : (
                  <IntegrationConnectButton
                    onClick={installQuickBooks}
                    disabled={exclusiveApiLocked}
                  />
                )}
              </NonTrialScope>
            </>
          }
        />

        <IntegrationRow
          icon={<IntegrationLogo id="xero" />}
          title="Xero"
          action={
            <>
              <TrialScope>
                <IntegrationDisabledButton />
              </TrialScope>
              <NonTrialScope>
                {xeroConnected ? (
                  <IntegrationConnectedButton />
                ) : (
                  <IntegrationConnectButton
                    onClick={installXero}
                    disabled={exclusiveApiLocked}
                  />
                )}
              </NonTrialScope>
            </>
          }
        />

        <IntegrationRow
          icon={<IntegrationLogo id="priority" />}
          title="Priority"
          action={
            <>
              <TrialScope>
                <IntegrationDisabledButton />
              </TrialScope>
              <NonTrialScope>
                {priorityShownConnected ? (
                  <IntegrationConnectedButton />
                ) : isViewzConnectBlocked(connected) ? (
                  <IntegrationConnectButton disabled />
                ) : (
                  <PriorityConnection />
                )}
              </NonTrialScope>
            </>
          }
        />

        <IntegrationRow
          icon={<IntegrationLogo id="stripe" />}
          title="Stripe"
          action={
            <>
              <TrialScope>
                <IntegrationDisabledButton />
              </TrialScope>
              <NonTrialScope>
                {stripeConnected ? (
                  <IntegrationConnectedButton />
                ) : exclusiveApiLocked && !stripeConnected ? (
                  <IntegrationConnectButton disabled />
                ) : (
                  <StripeConnection />
                )}
              </NonTrialScope>
            </>
          }
        />

        <IntegrationRow
          icon={<IntegrationLogo id="morning" />}
          title="Morning"
          action={
            <>
              <TrialScope>
                <IntegrationDisabledButton />
              </TrialScope>
              <NonTrialScope>
                {morningConnected ? (
                  <IntegrationConnectedButton />
                ) : exclusiveApiLocked && !morningConnected ? (
                  <IntegrationConnectButton disabled />
                ) : (
                  <MorningConnection />
                )}
              </NonTrialScope>
            </>
          }
        />

        {showViewz ? (
          <IntegrationRow
            icon={
              <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-slate-100 bg-white text-sm font-semibold text-slate-800">
                Vz
              </span>
            }
            title="Viewz"
            action={
              <>
                <TrialScope>
                  <IntegrationDisabledButton />
                </TrialScope>
                <NonTrialScope>
                  {viewzConnected ? (
                    <IntegrationConnectedButton />
                  ) : isViewzConnectBlocked(connected) ? (
                    <IntegrationConnectButton disabled />
                  ) : (
                    <ViewzConnection />
                  )}
                </NonTrialScope>
              </>
            }
          />
        ) : null}

        <IntegrationRow
          icon={
            <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-slate-100 bg-white text-emerald-700">
              <IconFileSpreadsheet size={28} stroke={1.75} />
            </span>
          }
          title="Excel / CSV import"
          action={
            <>
              <TrialScope>
                <IntegrationConnectButton onClick={() => setShowExcelUpload(true)}>
                  Upload Excel
                </IntegrationConnectButton>
              </TrialScope>
              <NonTrialScope>
                {excelConnected || !connected ? (
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {excelConnected ? <IntegrationConnectedButton /> : null}
                    <IntegrationConnectButton
                      onClick={() => setShowExcelUpload(true)}
                    >
                      Upload Excel
                    </IntegrationConnectButton>
                  </div>
                ) : (
                  <IntegrationConnectButton disabled>
                    Upload Excel
                  </IntegrationConnectButton>
                )}
              </NonTrialScope>
            </>
          }
        />
      </div>
    </div>
  );
}

function IntegrationRow({
  icon,
  title,
  action,
}: {
  icon: React.ReactNode;
  title: React.ReactNode;
  action: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4 rounded-[10px] border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {icon}
        <div className="min-w-0 text-base font-semibold text-slate-900">
          {title}
        </div>
      </div>
      <div className="flex shrink-0 justify-end sm:ml-4">{action}</div>
    </div>
  );
}
